import {memo, useCallback, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import DonutChart from '@/components/shared/charts/DonutChart';
import type {DonutSegment} from '@/components/shared/charts/DonutChart';
import {borderRadius, colors, contentMaxWidth, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';
import {COUNT_UP_DURATION_MS, useCountUp} from '@/hooks/useCountUp';

export interface DistributionChartProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** Rows, in display order. Each carries its own `colors.*` token. */
    items: DonutSegment[];
    /** Headline figure in the donut hole, e.g. "75%". */
    centerValue?: string;
    centerLabel?: string;
    /** Supporting note below the rows. Accepts rich text (`<strong>`, `<span>`). */
    footnote?: string;
}

// Two equal columns capped at 64rem and centred. Letting the rows column take all
// remaining container width (the `auto 1fr` it started as) stretched the bars to
// ~980px against a 260px donut, which is the width bug this fixes.
const gridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
    gap: {xs: 5, md: 8},
    alignItems: 'center',
    justifyItems: 'center',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const rowsWrapSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16],
    width: '100%'
} as const;

const rowHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[12],
    mb: spacing[8]
} as const;

const dotSx = {width: 8, height: 8, borderRadius: '50%', flexShrink: 0} as const;

const rowLabelSx = {
    flexGrow: 1,
    fontSize: fontSize[15],
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

// The value wears a text token, not the series colour: the orange and green steps
// sit at ~2:1 against the surface and would be unreadable as text. Identity is
// carried by the dot and the bar beside it.
const rowValueSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

// The track sits inside the row so the fill grows from a single shared baseline.
const trackSx = {
    position: 'relative' as const,
    height: 6,
    borderRadius: borderRadius.full,
    bgcolor: colors.neutral.borderLight,
    overflow: 'hidden'
} as const;

// The fill grows on the same cubic ease-out and duration as the counting figure
// beside it, so the bar and its number land together.
// No CSS transition: the width is read straight off the counting value, so the bar and
// its number are driven by one animation and there is no transition to introduce, snap
// back, or fight over. `useCountUp` already honours reduced-motion preferences.
const barSx = (item: DonutSegment, shown: number, max: number) =>
    ({
        height: '100%',
        width: `${(shown / max) * 100}%`,
        borderRadius: borderRadius.full,
        bgcolor: item.color
    } as const);

const footnoteSx = {
    mt: spacing[8],
    p: spacing[24],
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.default,
    fontSize: fontSize[15],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

/**
 * One labelled row: name, counting value and the bar that fills to match. Its own
 * component because `useCountUp` is a hook and so cannot run inside a `map`.
 *
 * `run` counts hovers: 0 is the resting state, which shows the real figure so nothing
 * looks half-drawn before anyone hovers, and every increment replays the count.
 */
const DistributionRow = ({item, max, run}: {item: DonutSegment; max: number; run: number}) => {
    const animating = run > 0;
    // Two decimals so the bar's width moves smoothly; the printed figure is rounded.
    const counted = useCountUp(item.value, 2, animating, COUNT_UP_DURATION_MS, run);
    const shown = animating ? counted : item.value;

    return (
        <Box>
            <Box sx={rowHeaderSx}>
                <Box sx={{...dotSx, bgcolor: item.color}} />
                <Typography component="p" className="font-regular-text" sx={rowLabelSx}>
                    {item.label}
                </Typography>
                <Typography component="p" className="font-headers" sx={rowValueSx}>
                    {Math.round(shown)}%
                </Typography>
            </Box>
            <Box sx={trackSx}>
                <Box sx={barSx(item, shown, max)} />
            </Box>
        </Box>
    );
};

/**
 * DistributionChart — part-to-whole section: a donut for the shape at a glance
 * beside labelled bars carrying the exact values, plus an optional footnote.
 *
 * Every row is directly labelled with its name and value, so identity never rests
 * on colour alone — required, since the categorical palette runs below 3:1 against
 * the section surface.
 */
const DistributionChart = ({eyebrow, heading, subheading, items, centerValue, centerLabel, footnote}: DistributionChartProps) => {
    const max = Math.max(...items.map((item) => item.value), 1);
    // Hovering the chart plays the counts once, on the first pointer entry only. At rest
    // (`run` 0) every figure and bar shows its real value.
    const [run, setRun] = useState(0);
    const played = useRef(false);
    const replay = useCallback(() => {
        if (played.current) return;
        played.current = true;
        setRun((current) => current + 1);
    }, []);

    return (
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />
            <Box sx={gridSx} onMouseEnter={replay}>
                <DonutChart segments={items} centerValue={centerValue} centerLabel={centerLabel} run={run} />

                <Box sx={rowsWrapSx}>
                    {items.map((item) => (
                        <DistributionRow key={item.label} item={item} max={max} run={run} />
                    ))}

                    {footnote && (
                        <Typography component="p" className="font-regular-text" sx={footnoteSx}>
                            {parse(footnote)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(DistributionChart);
